import React, { useContext } from 'react';

import cx from 'classnames';
import arrayMutators from 'final-form-arrays';
import { Modal } from 'modal.react.js';
import { isEmpty } from 'ramda';
import { Field, Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useSelector } from 'react-redux';
import { useNetworkState } from 'react-use';

import GuestForm from 'components/Pages/Admin/GuestPortal/GuestForms/GuestForm';
import { AModal } from 'components/shared/AModal';
import Button from 'components/shared/Button';
import Divider from 'components/shared/Divider';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import Icon from 'components/shared/Icon';
import Trash2 from 'components/shared/Icon/icons/Trash2';
import LandingPageContext from 'components/shared/LandingPage/contexts/LandingPageContext';
import FormTitle from 'components/shared/Typography/FormTitle';
import Text from 'components/shared/Typography/Text';
import i18n from 'i18n';
import { modalDataSelector } from 'store/modal/selectors';
import { getGuestNameInfo } from 'utils/itineraries/functions';

import area from '../../../../../shared/GlobalModalArea/area';
import { Subtitle } from '../shared/Subtitle';
import Autosave from './Autosave';
import { useGuestForms } from './functions';
import styles from './index.module.css';

const Guests = () => {
  const {
    handleDeleteGuest,
    handleUpdateGuest,
    handleCreateGuest,
    createGuest,
    updateGuest,
    sendGuestForms,
    isSendingGuestForms,
    closeGuestForm,
    checkFieldAvailability,
    isModalOpened,
    additionalInputProps,
    updateGuestPortal,
    isInsideItinerariesItemBuilder,
    onFormDataUpdated,
  } = useGuestForms();
  const { page: guestPortal, isSamplePreview } = useContext(LandingPageContext);
  const modalData = useSelector(modalDataSelector);
  const { online } = useNetworkState();

  return (
    <div
      className={cx(
        styles.container,
        'custom-guest-form-container',
        isInsideItinerariesItemBuilder && styles.containerMarginTop,
      )}
    >
      <Subtitle title={i18n.t('proposal_content.guest_portal.guest_form.description.title')}>
        {i18n.t('proposal_content.guest_portal.guest_form.description.subtitle')}
      </Subtitle>

      <Divider className={styles.divider} isMarginSm />

      {guestPortal?.guests?.map((guest, idx) => {
        const guestNameInfo = getGuestNameInfo(guest);

        return (
          <div key={guest.id} className={cx(styles.guestItem, 'custom-guest-form-item')}>
            <div className={styles.guestItemHead}>
              <div className={cx(styles.guestItemNumber, 'custom-guest-form-item-number')}>{idx + 1}.</div>
              <Text
                accent
                className={cx(
                  styles.listItemName,
                  'custom-guest-form-item-name',
                  guestNameInfo.isEmpty && styles.listItemNameUntitled,
                  guestNameInfo.isEmpty && 'custom-guest-form-item-name-empty',
                )}
              >
                {guestNameInfo.name}
              </Text>
            </div>
            {!isSamplePreview && (
              <div className={cx(styles.listItemActions, 'custom-guest-form-item-actions')}>
                <Text
                  accent
                  className={cx(styles.listItemEdit, 'custom-guest-form-item-edit')}
                  onClick={() => handleUpdateGuest(guest)}
                >
                  {i18n.t('common.buttons.edit')}
                </Text>
                {online && (
                  <button
                    type="button"
                    className={cx(styles.listItemBtn, 'custom-guest-form-item-btn')}
                    onClick={() => handleDeleteGuest(guest)}
                  >
                    <Icon
                      component={Trash2}
                      stroke="#3E3E40"
                      className={cx(styles.icon, styles.deleteIcon, 'custom-guest-form-item-delete-icon')}
                    />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {online && (
        <Button
          disabled={isSamplePreview}
          htmlType="button"
          className={cx(styles.mainBtn, 'custom-guest-form-main-btn')}
          onClick={handleCreateGuest}
        >
          {i18n.t('proposal_content.guest_portal.guest_form.buttons.add_guest')}
        </Button>
      )}

      <Form
        onSubmit={updateGuestPortal}
        initialValues={{
          specialOccasions: guestPortal.specialOccasions,
          additionalInfo: guestPortal.additionalInfo,
        }}
        mutators={arrayMutators}
      >
        {({ handleSubmit }) => (
          <form
            onSubmit={handleSubmit}
            className={cx(styles.mainForm, 'custom-guest-form-main-form')}
            autoComplete="nope"
          >
            {!isInsideItinerariesItemBuilder && <Autosave debounce={700} save={updateGuestPortal} />}

            <Subtitle title={i18n.t('proposal_content.guest_portal.guest_form.special_occasions.title')} />

            <Field
              component={FormGroupAdapter}
              name="specialOccasions"
              type="textarea"
              label={i18n.t('proposal_content.guest_portal.guest_form.special_occasions.description')}
              placeholder={i18n.t('proposal_content.guest_portal.guest_form.special_occasions.placeholder')}
              minRows={2}
              className={styles.specialOccasionsWrapper}
              onClick={checkFieldAvailability}
              withBackground
              noTransform
              parse={(v) => v || null}
              disabled={!online || isSamplePreview}
              {...additionalInputProps}
            />

            {!isEmpty(guestPortal.additionalInfo) && (
              <>
                <FormTitle className={cx(styles.mainFormTitle, 'custom-guest-main-form-title')} noBorder>
                  {i18n.t('proposal_content.guest_portal.guest_form.additional_information.title')}
                </FormTitle>
                <FieldArray name="additionalInfo">
                  {({ fields }) => (
                    <>
                      {fields.map((name, index) => {
                        return (
                          <Field
                            key={name}
                            component={FormGroupAdapter}
                            name={`${name}.answer`}
                            type="textarea"
                            minRows={2}
                            className={cx(styles.answerContainer, 'custom-guest-form-answer-container')}
                            label={guestPortal.additionalInfo[index].question}
                            placeholder={i18n.t(
                              'proposal_content.guest_portal.guest_form.additional_information.placeholder',
                            )}
                            parse={(v) => v || null}
                            onClick={checkFieldAvailability}
                            noTransform
                            withBackground
                            autoComplete="off"
                            disabled={!online || isSamplePreview}
                            {...additionalInputProps}
                          />
                        );
                      })}
                    </>
                  )}
                </FieldArray>
              </>
            )}
          </form>
        )}
      </Form>

      {online && (
        <div className={cx(styles.notifyBlock, 'custom-guests-notify-consultant')}>
          <Subtitle title={i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.modal.title')}>
            <span>
              {/* eslint-disable-next-line react/no-unescaped-entities */}
              {i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.modal.description')}
            </span>
          </Subtitle>

          <FormActions>
            <Button
              onClick={sendGuestForms}
              htmlType="button"
              className={cx(styles.sendNotifBtn, 'custom-guest-form-send-notif-btn')}
              disabled={isSendingGuestForms || isSamplePreview}
            >
              {i18n.t('proposal_content.guest_portal.guest_form.notify_consultant.send')}
            </Button>
          </FormActions>
        </div>
      )}

      {isModalOpened && (
        <Modal area={area}>
          <AModal
            header={
              modalData.guest && !isEmpty(modalData.guest)
                ? i18n.t('proposal_content.guest_portal.guest_form.edit_guest')
                : i18n.t('proposal_content.guest_portal.guest_form.add_guest')
            }
            isOpen
            onClose={() => closeGuestForm()}
          >
            <GuestForm
              onSubmit={modalData.mode === 'create' ? createGuest : updateGuest}
              guest={modalData.guest}
              saveAndNotify={sendGuestForms}
              onFormDataUpdated={onFormDataUpdated}
              travelEndDate={guestPortal?.itinerary?.travelEndDate}
              isLanding
            />
          </AModal>
        </Modal>
      )}
    </div>
  );
};

export default Guests;
